import React, { useState, useEffect, useCallback } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { Button, TextInput, Dropdown } from "../indexComponents";
import { faXmark } from "@fortawesome/free-solid-svg-icons";

const MyCalendar = ({ isTeam, teamId }) => {
  const [value, onChange] = useState(new Date());
  const [formattedDate, setFormattedDate] = useState(getFormattedDate(value));
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [eventForm, setEventForm] = useState(false);
  const [newEventName, setNewEventName] = useState("");
  const [newEventDate, setNewEventDate] = useState("");
  const [events, setEvents] = useState([]);
  const [color, setColor] = useState("");
  const [categorie, setCategorie] = useState("");
  const [categories, setCategories] = useState("");
  const [categoriesOptions, setCategoriesOptions] = useState([]);
  const recurrenceOptions = [
    { value: "", label: "Unique" },
    { value: "weekly", label: "Hebdomadaire" },
    { value: "monthly", label: "Mensuel" },
    { value: "yearly", label: "Annuel" },
  ];
  const [recurrence, setRecurrence] = useState("");

  function formatDate(dateString) {
    const [day, month, year] = dateString.split("/");
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const fetchEvents = useCallback(async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/events/${isTeam}/${
          teamId ? teamId : 0
        }`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to get events");
      }

      const data = await response.json();
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Set to midnight to compare dates correctly

      const formattedEvents = data.flatMap((event) => {
        const date = formatDate(event.date); // Convert date string to Date object
        const recurrence = event.recurrence;

        // If this event is not recurring, just return it as is
        if (!recurrence) {
          return {
            ...event,
            date,
          };
        }

        // Otherwise, generate recurring events for the next year
        const recurringEvents = [];
        for (let i = 0; i <= 12; i++) {
          const newDate = new Date(date);
          if (recurrence === "monthly") {
            newDate.setMonth(date.getMonth() + i);
          } else if (recurrence === "weekly") {
            newDate.setDate(date.getDate() + i * 7);
          } else if (recurrence === "yearly") {
            newDate.setFullYear(date.getFullYear() + i);
          }

          recurringEvents.push({
            ...event,
            date: newDate,
          });
        }

        return recurringEvents;
      });
      const upcomingEvents = formattedEvents
        .filter((event) => event.date >= today)
        .sort((a, b) => a.date - b.date);

      setEvents(upcomingEvents);

      const tempArray = data.map((event) => event.categorie);
      const uniqueCategories = Array.from(
        new Set(tempArray.map((cat) => JSON.stringify(cat)))
      ).map((cat) => JSON.parse(cat));

      const categoriesOptions = uniqueCategories.map((categorie) => ({
        value: categorie.color,
        label: categorie.name,
      }));
      setCategoriesOptions(categoriesOptions);
    } catch (error) {
      console.error("Error fetching events:", error);
    }
  }, [isTeam, teamId]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const onDateChange = useCallback(
    (date) => {
      onChange(date);
      setFormattedDate(getFormattedDate(date));
      const foundEvents = events.filter(
        (event) => event.date.getTime() === date.getTime()
      );
      setSelectedEvents(foundEvents);
    },
    [events]
  );

  function getFormattedDate(date) {
    const formatDate = new Date(date);
    const options = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return formatDate.toLocaleDateString("fr-FR", options);
  }

  function getNewEventDate(date) {
    const splitDate = date.split("-");
    const reverseDate = splitDate.reverse();
    const newDate = reverseDate.join("/");
    return newDate;
  }

  const handleNewEvent = async () => {
    const formatedDate = getNewEventDate(newEventDate);

    const response = await fetch(`${process.env.REACT_APP_API_URL}/events`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify({
        name: newEventName,
        date: formatedDate,
        isTeam: isTeam,
        teamId: teamId,
        recurrence: recurrence,
        categorie: { name: categorie, color: color },
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to add event");
    }

    setEventForm(false);
    setNewEventName("");
    setNewEventDate("");
    setRecurrence("");
    fetchEvents();
  };

  const deleteEvent = async (eventId, index) => {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/events/${eventId}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to delete event");
    }

    const tempArray = [...selectedEvents];
    tempArray.splice(index, 1);
    setSelectedEvents(tempArray);
    fetchEvents();
  };

  const handleRecurrenceChange = (selectedOption) => {
    setRecurrence(selectedOption ? selectedOption.value : null);
  };

  const handleCategorieChange = (selectedOption) => {
    setCategorie(selectedOption ? selectedOption.label : null);
    setColor(selectedOption.value);
  };

  return (
    <div className="calendar-frame">
      <div className="calendar-container">
        <Calendar
          onChange={onDateChange}
          value={value}
          tileContent={({ date, view }) =>
            view === "month" &&
            events.some(
              (eventDate) => eventDate.date.getTime() === date.getTime()
            ) && (
              <div className="event-markers">
                {events
                  .filter(
                    (eventDate) => eventDate.date.getTime() === date.getTime()
                  )
                  .map((event, index) => (
                    <p
                      key={index}
                      className="event-marker"
                      style={{ color: event.categorie?.color }}
                    >
                      •
                    </p>
                  ))}
              </div>
            )
          }
        />
        <div className="selected-date-container">
          <div className="flex col h-100 w-100">
            <h2
              className="active-date"
              onClick={() => console.log("test", color)}
            >
              {formattedDate}
            </h2>
            <div className="event-list-container">
              {selectedEvents.map((event, index) => (
                <div key={index} className="event-container w-100">
                  <p className="event">- {event.name}</p>
                  <Button
                    icon={faXmark}
                    className="delete-event"
                    onClick={() => deleteEvent(event.id, index)}
                  />
                </div>
              ))}
            </div>
            {selectedEvents.length < 1 ? (
              <div className="no-event-container">
                <h2>Aucun évènement</h2>
              </div>
            ) : (
              ""
            )}
          </div>
          {eventForm ? (
            <div className="add-event-form">
              <TextInput
                placeholder="Titre"
                value={newEventName}
                onChange={(e) => setNewEventName(e.target.value)}
              />
              <TextInput
                type="date"
                value={newEventDate}
                onChange={(e) => setNewEventDate(e.target.value)}
              />
              <Dropdown
                options={recurrenceOptions}
                onChange={handleRecurrenceChange}
                value={recurrence.value}
                placeholder="Réccurence"
              />

              <Dropdown
                options={categoriesOptions}
                onChange={handleCategorieChange}
                value={categorie.name}
                placeholder="Catégorie"
              />
              <TextInput
                type="color"
                labelText="Couleur"
                value={color}
                onChange={(e) => setColor(e.target.value)}
              />
              <TextInput
                type="text"
                labelText="Nom de la catégorie"
                value={categorie}
                onChange={(e) => setCategorie(e.target.value)}
              />

              <div className="btn-container flex jc-space-between margin-t-1">
                <Button
                  text="Enregistrer"
                  className="add-event-btn"
                  onClick={handleNewEvent}
                />
                <Button
                  text="Annuler"
                  className="add-event-btn"
                  onClick={() => setEventForm(!eventForm)}
                />
              </div>
            </div>
          ) : (
            <Button
              text="Ajouter un évènement"
              className="add-event-btn mg-t-1"
              onClick={() => setEventForm(!eventForm)}
            />
          )}
        </div>
      </div>
      <div className="futurs-events">
        <h2>Évènements à venir</h2>
        <div className="next-event-container">
          {events.slice(0, 4).map((event, index) => (
            <div
              className="flex gap-15 futur-event"
              key={index}
              style={{ backgroundColor: `${event.categorie.color}50` }} // Ajouter l'arrière-plan léger ici
            >
              <p className="event-name">{event.name}</p>
              <p className="event-date" style={{ color: event.color }}>
                {getFormattedDate(event.date)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MyCalendar;
